import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { ProtectedRoute, GuestOnlyRoute } from '@/routes/ProtectedRoute';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';

// Every authenticated page is route-split — at 17 modules the
// single-bundle bundle crossed Vite's 800kB warning threshold, and
// none of these need to be in the initial chunk: the auth pages above
// (needed before login) stay eager, everything behind ProtectedRoute
// loads on first navigation to that route.
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const MoviesListPage = lazy(() => import('@/features/movies/MoviesListPage').then((m) => ({ default: m.MoviesListPage })));
const MovieFormPage = lazy(() => import('@/features/movies/MovieFormPage').then((m) => ({ default: m.MovieFormPage })));
const SeriesListPage = lazy(() => import('@/features/series/SeriesListPage').then((m) => ({ default: m.SeriesListPage })));
const SeriesFormPage = lazy(() => import('@/features/series/SeriesFormPage').then((m) => ({ default: m.SeriesFormPage })));
const SeasonDetailPage = lazy(() => import('@/features/series/SeasonDetailPage').then((m) => ({ default: m.SeasonDetailPage })));
const EpisodeFormPage = lazy(() => import('@/features/series/EpisodeFormPage').then((m) => ({ default: m.EpisodeFormPage })));
const GenresPage = lazy(() => import('@/features/taxonomy/GenresPage').then((m) => ({ default: m.GenresPage })));
const CategoriesPage = lazy(() => import('@/features/taxonomy/CategoriesPage').then((m) => ({ default: m.CategoriesPage })));
const LanguagesPage = lazy(() => import('@/features/taxonomy/LanguagesPage').then((m) => ({ default: m.LanguagesPage })));
const StudiosPage = lazy(() => import('@/features/taxonomy/StudiosPage').then((m) => ({ default: m.StudiosPage })));
const ActorsPage = lazy(() => import('@/features/taxonomy/ActorsPage').then((m) => ({ default: m.ActorsPage })));
const LiveTvCategoriesPage = lazy(() => import('@/features/taxonomy/LiveTvCategoriesPage').then((m) => ({ default: m.LiveTvCategoriesPage })));
const BannersPage = lazy(() => import('@/features/banners/BannersPage').then((m) => ({ default: m.BannersPage })));
const UsersListPage = lazy(() => import('@/features/users/UsersListPage').then((m) => ({ default: m.UsersListPage })));
const UserCreatePage = lazy(() => import('@/features/users/UserCreatePage').then((m) => ({ default: m.UserCreatePage })));
const UserDetailPage = lazy(() => import('@/features/users/UserDetailPage').then((m) => ({ default: m.UserDetailPage })));
const PlansPage = lazy(() => import('@/features/plans/PlansPage').then((m) => ({ default: m.PlansPage })));
const SubscriptionsListPage = lazy(() => import('@/features/subscriptions/SubscriptionsListPage').then((m) => ({ default: m.SubscriptionsListPage })));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const LiveTvListPage = lazy(() => import('@/features/live-tv/LiveTvListPage').then((m) => ({ default: m.LiveTvListPage })));
const LiveTvFormPage = lazy(() => import('@/features/live-tv/LiveTvFormPage').then((m) => ({ default: m.LiveTvFormPage })));
const AdultProvidersListPage = lazy(() => import('@/features/adult/AdultProvidersListPage').then((m) => ({ default: m.AdultProvidersListPage })));
const AdultProviderFormPage = lazy(() => import('@/features/adult/AdultProviderFormPage').then((m) => ({ default: m.AdultProviderFormPage })));
const AdultProviderSectionsPage = lazy(() =>
  import('@/features/adult/AdultProviderSectionsPage').then((m) => ({ default: m.AdultProviderSectionsPage })),
);

function withSuspense(element: ReactNode): ReactNode {
  return <Suspense fallback={<FullPageSpinner />}>{element}</Suspense>;
}

/**
 * Routes are added here as each module is built (see docs/README.md
 * "Module checklist"). Keeping this file as the single composition
 * point mirrors the backend's src/Routes/api.php pattern.
 */
export const router = createBrowserRouter([
  {
    element: <GuestOnlyRoute />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: withSuspense(<DashboardPage />) },
          { path: 'profile', element: withSuspense(<ProfilePage />) },
          { path: 'movies', element: withSuspense(<MoviesListPage />) },
          { path: 'movies/new', element: withSuspense(<MovieFormPage />) },
          { path: 'movies/:id', element: withSuspense(<MovieFormPage />) },
          { path: 'series', element: withSuspense(<SeriesListPage />) },
          { path: 'series/new', element: withSuspense(<SeriesFormPage />) },
          { path: 'series/:id', element: withSuspense(<SeriesFormPage />) },
          { path: 'series/:seriesId/seasons/:seasonId', element: withSuspense(<SeasonDetailPage />) },
          { path: 'episodes/:id', element: withSuspense(<EpisodeFormPage />) },
          { path: 'taxonomy/genres', element: withSuspense(<GenresPage />) },
          { path: 'taxonomy/categories', element: withSuspense(<CategoriesPage />) },
          { path: 'taxonomy/languages', element: withSuspense(<LanguagesPage />) },
          { path: 'taxonomy/studios', element: withSuspense(<StudiosPage />) },
          { path: 'taxonomy/actors', element: withSuspense(<ActorsPage />) },
          { path: 'taxonomy/live-tv-categories', element: withSuspense(<LiveTvCategoriesPage />) },
          { path: 'banners', element: withSuspense(<BannersPage />) },
          { path: 'users', element: withSuspense(<UsersListPage />) },
          { path: 'users/new', element: withSuspense(<UserCreatePage />) },
          { path: 'users/:id', element: withSuspense(<UserDetailPage />) },
          { path: 'plans', element: withSuspense(<PlansPage />) },
          { path: 'subscriptions', element: withSuspense(<SubscriptionsListPage />) },
          { path: 'live-tv', element: withSuspense(<LiveTvListPage />) },
          { path: 'live-tv/new', element: withSuspense(<LiveTvFormPage />) },
          { path: 'live-tv/:id', element: withSuspense(<LiveTvFormPage />) },
          { path: 'adult-providers', element: withSuspense(<AdultProvidersListPage />) },
          { path: 'adult-providers/new', element: withSuspense(<AdultProviderFormPage />) },
          { path: 'adult-providers/:id', element: withSuspense(<AdultProviderFormPage />) },
          { path: 'adult-providers/:id/sections', element: withSuspense(<AdultProviderSectionsPage />) },
          { path: 'settings', element: withSuspense(<SettingsPage />) },
          { path: 'notifications', element: withSuspense(<NotificationsPage />) },
          { path: 'analytics', element: withSuspense(<AnalyticsPage />) },
        ],
      },
    ],
  },
]);
